import React from 'react';

interface NumberInputProps {
  label: string;
  value: number | string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  /** When set, display is formatted to this many decimal places (e.g. 2 for currency) */
  decimals?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}

const NumberInput: React.FC<NumberInputProps> = ({
  label, value, required, min, max, step, decimals, disabled, onChange,
}) => {
  // Local editing string — only used when `decimals` is set.
  const [editValue, setEditValue] = React.useState('');
  const [focused, setFocused] = React.useState(false);

  // Keep editValue in sync when the external value changes while not focused.
  React.useEffect(() => {
    if (!focused && decimals !== undefined) {
      setEditValue(Number(value).toFixed(decimals));
    }
  }, [value, focused, decimals]);

  if (decimals !== undefined) {
    const displayed = focused ? editValue : Number(value).toFixed(decimals);
    return (
      <div className="mb-3">
        <label className="form-label">
          {label}
          {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
        </label>
        <input
          type="text"
          inputMode="decimal"
          className="form-control"
          value={displayed}
          required={required}
          disabled={disabled}
          onFocus={e => {
            setFocused(true);
            setEditValue(String(value));
            // Select all on focus for easy overwrite
            e.target.select();
          }}
          onChange={e => setEditValue(e.target.value)}
          onBlur={() => {
            setFocused(false);
            // Accept comma or dot as decimal separator
            const parsed = parseFloat(editValue.replace(',', '.'));
            const safe = isNaN(parsed) ? 0 : parsed;
            onChange(safe);
            setEditValue(safe.toFixed(decimals));
          }}
        />
      </div>
    );
  }

  // Default: plain numeric input (unchanged behaviour)
  return (
    <div className="mb-3">
      <label className="form-label">
        {label}
        {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
      </label>
      <input
        type="number"
        className="form-control"
        value={value}
        required={required}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={e => onChange(Number(e.target.value))}
      />
    </div>
  );
};

export default NumberInput;
